import { NgTemplateOutlet } from "@angular/common";
import {
  afterNextRender,
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  signal,
  viewChild
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";

import { filter } from "rxjs";
import { NzDrawerModule } from "ng-zorro-antd/drawer";
import {
  LktInfiniteScrollDirective,
  LktNotificationCloseReason,
  LktNotificationEmptyComponent,
  LktNotificationFilter,
  LktNotificationItem,
  LktNotificationItemComponent,
  LktNotificationPanelComponent,
  LktNotificationPreferenceChannel,
  LktNotificationPreferenceRow,
  LktNotificationPreferencesComponent
} from "@frontends-v3/design-system";

import {
  NOTIFICATIONS_QUERY_PARAM,
  NOTIFICATIONS_QUERY_PREFERENCES
} from "../../constants/notifications.constants";
import { NotificationNavigationService } from "../../services/notifications/notification-navigation.service";
import { NotificationsStateService } from "../../services/notifications/notifications-state.service";

import { buildNotificationItem } from "./utils/build-notification-item.util";
import { buildNotificationPreferenceRow } from "./utils/build-notification-preference-row.util";

/**
 * Fallback do tuck (pedaço do painel escondido atrás do rail) para o caso raro de o computed vir
 * vazio. O valor de VERDADE é o `padding-left` computado do painel (SCSS,
 * `notification-panel.component.scss`, bloco `.notifications-panel--floating`, `--nt-tuck`) — lido
 * em `anchorToBell()`, mesma leitura que `js/notificacoes.js` faz (`getComputedStyle(el).paddingLeft`).
 * Isso deixa o CSS como fonte única do número: mudar `--nt-tuck` no SCSS não pede mudança aqui.
 */
const TUCK_FALLBACK_PX = 20;
const MOBILE_QUERY = "(max-width: 767px)";
/** Seletor do card do rail, para medir onde o painel encosta. */
const RAIL_SELECTOR = ".sidebar-menu";
/**
 * Os sinos, em ordem de preferência, para o caso em que o painel NÃO foi aberto por clique (deep
 * link `?notificacoes=preferencias`): sem âncora o painel de desktop fica sem `top`/`left` e é
 * pintado no canto superior esquerdo. O primeiro é o do rail (desktop), o segundo o da bottom-nav
 * (mobile) — e a escolha é por VISIBILIDADE, não por breakpoint, porque as duas superfícies existem
 * no DOM dos dois apps e é o CSS que esconde uma de cada vez.
 *
 * Sim, é `data-cy` em seletor de runtime, e de propósito: é o único identificador estável dos dois
 * sinos (um vem do design-system, o outro do shell de cada app) e há precedente no próprio módulo
 * (`notification-panel.component.ts` devolve o foco por `data-cy`). Renomear qualquer um dos dois
 * quebra COMPORTAMENTO, não só teste de E2E.
 */
const BELL_SELECTORS: readonly string[] = [
  '[data-cy="sidebar-notifications"]',
  '[data-cy="bottom-nav-notifications"]'
];

/**
 * Container do painel de notificações (DEV-872). Montado nos shells do admin e da plataforma.
 *
 * Único ponto que injeta o estado: os componentes do design-system só recebem inputs.
 *
 * No desktop o painel é `position: fixed` e a posição vem de `getBoundingClientRect()` do sino
 * visível. No mobile ele vira `nz-drawer` ancorado embaixo.
 */
@Component({
  selector: "app-notifications-panel",
  standalone: true,
  imports: [
    NgTemplateOutlet,
    NzDrawerModule,
    LktInfiniteScrollDirective,
    LktNotificationPanelComponent,
    LktNotificationItemComponent,
    LktNotificationEmptyComponent,
    LktNotificationPreferencesComponent
  ],
  templateUrl: "./notifications-panel.container.html",
  styleUrl: "./notifications-panel.container.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationsPanelContainerComponent {
  private readonly router = inject(Router);
  /**
   * Só para LER o query param do deep link. Query param é global à URL, então o snapshot de
   * qualquer rota ativa devolve o mesmo mapa — não importa que este container não seja componente
   * de rota. Navegar continua sendo do `Router` (e o salto entre apps, do
   * `NotificationNavigationService`).
   */
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly navigation = inject(NotificationNavigationService);
  public readonly state = inject(NotificationsStateService);

  // `read: ElementRef` é obrigatório aqui: `#panelRef` está num elemento de componente
  // (`<lkt-notification-panel>`), e sem essa opção a query resolveria para a instância do
  // componente, não para o ElementRef do seu nó nativo. Mesmo intenção do precedente de
  // `sidebar-menu.component.ts:bellIconRef` — mas aquele, com um único parâmetro de tipo
  // explícito, cai no overload SEM `read` do `viewChild` (o `read: ElementRef` vira propriedade
  // desconhecida, TS2353, e quebra o build AOT; visto ao rodar `nx build platform`). O overload
  // com `read` exige dois parâmetros de tipo — `<LocatorT, ReadT>` —, por isso os dois aqui.
  private readonly panelRef = viewChild<LktNotificationPanelComponent, ElementRef<HTMLElement>>(
    "panelRef",
    { read: ElementRef }
  );
  /** Instância do painel, para a API de rolagem (`scrollTop` / `restoreScrollTop`). */
  private readonly panelComponent = viewChild(LktNotificationPanelComponent);

  /**
   * Bookkeeping imperativo do painel: rolagem da lista guardada ao entrar em preferências (CA 02) e
   * o estado anterior de abertura, para focar só na transição.
   *
   * `consumingDeepLink` fecha a janela em que as DUAS portas de leitura do deep link
   * (`afterNextRender` e `NavigationEnd`) veriam o mesmo parâmetro: enquanto a navegação de limpeza
   * está em voo o snapshot ainda o carrega, e a segunda porta navegaria e abriria de novo — duas
   * cargas de preferências e duas da lista, por um clique só.
   *
   * É SEGURO, não medido: nas duas ordens de montagem que dá para reproduzir em teste (container
   * criado dentro da ativação da rota e container criado depois de a navegação assentar) a limpeza
   * sempre termina antes de a segunda porta ler, então nenhuma mutação desta trava fica vermelha.
   * Ela existe porque a ordem entre o primeiro render e a conclusão de uma navegação não é
   * contratual no Angular, e o preço de errar é pago em requisição duplicada a cada clique no link
   * do e-mail. Se algum dia ela ficar no caminho, remova-a com a medição na mão.
   *
   * Objeto `readonly` com campos mutáveis, e não `signal()`: os dois primeiros são lidos DENTRO de
   * `afterRenderEffect`, e virar signal criaria dependência do efeito no que ele mesmo escreve. O
   * `readonly` também é o que satisfaz o hook `enforce-signals`.
   */
  private readonly cursor = { listScrollTop: 0, wasOpen: false, consumingDeepLink: false };

  private readonly isMobile = signal<boolean>(false);
  public readonly isDesktop = computed<boolean>(() => !this.isMobile());

  /**
   * A conversão para view-model precisa de um `now`, e por isso não vive no estado: o "há 12 min"
   * congelaria no instante da carga. Aqui ela é refeita a cada mudança da lista.
   */
  public readonly viewItems = computed<LktNotificationItem[]>(() => {
    const now = new Date();
    return this.state.items().map((dto) => buildNotificationItem(dto, now));
  });

  public readonly emptyTitle = computed<string>(() =>
    this.state.filter() === "unread" ? "Tudo lido por aqui" : "Nenhuma notificação ainda"
  );

  public readonly emptyDescription = computed<string>(() =>
    this.state.filter() === "unread"
      ? "Você está em dia com o que aconteceu nas suas turmas."
      : "Avisos sobre suas turmas e conteúdos aparecem aqui."
  );

  /**
   * Achado 2 (Important, fix round final): antes bastava `status() !== "loading"`, então uma falha
   * de carga (`status === "error"`) caía neste mesmo ramo vazio e afirmava "Nenhuma notificação
   * ainda" / "Você está em dia" — o único lugar da interface em que ela MENTE, num produto que leva
   * avisos `Critical` a gestores escolares. Exigir `"ready"` deixa `showError` cuidar do caso de
   * falha, em vez de os dois se confundirem.
   */
  public readonly showEmpty = computed<boolean>(
    () => this.state.status() === "ready" && this.viewItems().length === 0
  );

  /** Idem: `status` já era definido e testado no serviço, mas nenhum template de produção o lia. */
  public readonly showError = computed<boolean>(() => this.state.status() === "error");

  /**
   * DTO → linha visual, com a inversão de polaridade do canal (o contrato registra o DESLIGAMENTO,
   * a tela mostra o LIGADO). Fica aqui, e não no estado, pelo mesmo motivo de `viewItems`: o estado
   * guarda o que o servidor disse, o container traduz para o vocabulário do design-system.
   */
  public readonly preferenceRows = computed<LktNotificationPreferenceRow[]>(() =>
    this.state.preferences().groups.map((group) => buildNotificationPreferenceRow(group))
  );

  public readonly criticalAlertRows = computed<LktNotificationPreferenceRow[]>(() =>
    this.state.preferences().criticalAlerts.map((alert) => buildNotificationPreferenceRow(alert))
  );

  /**
   * Assinatura do que pode mudar a ALTURA do painel (panel-reanchor-fix, DEV-872) — por VALOR, não
   * pela identidade dos arrays de entrada. Um `computed()` só marca os consumidores como sujos
   * quando o RESULTADO muda, e é isso que faz `markRead` (troca a identidade de `state.items()` sem
   * mudar quantas linhas existem — o marcador de não lida fica `visibility: hidden` em vez de sumir
   * do DOM, `notification-item.component.html`, exatamente para não causar reflow da linha) e o
   * toggle de uma preferência (idem: `setPreference` troca a identidade do objeto sem mudar
   * `groups.length`/`criticalAlerts.length`) NÃO disparar `anchorToBell()` de novo — o efeito abaixo
   * só lê esta assinatura, nunca os arrays inteiros.
   *
   * Cobre os quatro gatilhos do brief (panel-reanchor-fix.md): itens da lista, status de carga
   * (`loading` → `ready`/`error` alterna entre lista, vazio e erro), a sentinela de `hasMore`, e na
   * vista de preferências a quantidade de linhas e o status daquela carga. `groups.length` e
   * `criticalAlerts.length` entram separados (não somados): um cenário em que um cresce e o outro
   * encolhe na mesma resposta não pode se cancelar e passar por "sem mudança".
   */
  private readonly anchorHeightSignature = computed<string>(() => {
    const preferences = this.state.preferences();
    return [
      this.state.items().length,
      this.state.status(),
      this.state.hasMore(),
      preferences.groups.length,
      preferences.criticalAlerts.length,
      this.state.preferencesStatus()
    ].join("|");
  });

  /** Copy do erro de carga: nunca afirma ausência, sempre nomeia a falha e oferece tentar de novo. */
  public readonly errorTitle: string = "Não foi possível carregar suas notificações";
  public readonly errorDescription: string = "Verifique sua conexão e tente novamente.";

  public constructor() {
    // Arranque do módulo: este container está montado no shell dos dois apps e existe desde o
    // primeiro render, então é o ponto único de `init()`. `init()` é idempotente.
    this.state.init();
    this.syncBreakpoint();

    // Navegar fecha o painel: o mesmo gesto que troca de tela não deve deixar overlay aberto.
    // O deep link é lido DEPOIS do fechamento, na mesma volta: uma navegação que carregue
    // `?notificacoes=preferencias` fecharia o painel que ela mesma pediu se a ordem fosse a inversa.
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.state.closePanel();
        this.consumeDeepLink();
      });

    // Segunda porta de leitura, e não a principal: medido no Angular 21, o shell roteado é criado
    // DENTRO da ativação, e o container junto com ele, então a assinatura acima já vê o
    // `NavigationEnd` da navegação inicial — a entrada pelo e-mail normalmente é consumida por ela.
    // `afterNextRender` cobre a montagem que chega DEPOIS de a navegação assentar (o container é um
    // filho de template do shell, e um dia pode passar a ser instanciado mais tarde — por `@defer`,
    // por uma vista lazy, ou por qualquer mudança na árvore do shell); nesse caso ele é a única
    // porta. Quando as duas veem o mesmo parâmetro, a trava `consumingDeepLink` garante uma
    // abertura só.
    afterNextRender(() => this.consumeDeepLink());

    // Reancora quando abre, quando a vista troca de largura (392 → 444) e quando o CONTEÚDO pode ter
    // mudado de altura (panel-reanchor-fix, DEV-872): na primeira abertura a lista ainda está
    // carregando, o painel nasce curto (só cabeçalho + rodapé) e é ancorado para aquela altura; sem
    // reler `anchorHeightSignature()` aqui, nada recalculava `top` quando os itens chegavam, e o
    // painel crescia para BAIXO a partir do topo já fixado, saindo do rail. `afterRenderEffect` roda
    // depois do render, então observar a mesma assinatura que o conteúdo observa dá exatamente
    // "reancore depois que o conteúdo mudou" — a mesma fidelidade do protótipo, que reancora ao fim
    // de toda repintura do painel (`js/notificacoes.js:730`, dentro de `pintarPainel()`).
    afterRenderEffect(() => {
      const open = this.state.panelOpen();
      this.state.view();
      this.anchorHeightSignature();
      if (open && this.isDesktop()) {
        this.anchorToBell();
      }
    });

    // Foco entra no painel só na transição de fechado para aberto: refocar em todo render roubaria
    // o foco dos controles internos enquanto a pessoa navega dentro do painel.
    afterRenderEffect(() => {
      const open = this.state.panelOpen();
      if (open && !this.cursor.wasOpen) {
        this.panelRef()?.nativeElement.focus();
      }
      this.cursor.wasOpen = open;
    });

    // CA 02: voltar de preferências devolve a lista onde ela estava. Dentro da própria lista a
    // rolagem já se preserva sozinha, porque o `@for` usa `track item.id` e reaproveita os nós —
    // diferente do protótipo, que refazia o `innerHTML` inteiro e perdia a posição.
    afterRenderEffect(() => {
      if (this.state.view() !== "list" || this.cursor.listScrollTop === 0) {
        return;
      }
      this.panelComponent()?.restoreScrollTop(this.cursor.listScrollTop);
      this.cursor.listScrollTop = 0;
    });
  }

  @HostListener("window:resize")
  public onWindowResize(): void {
    this.syncBreakpoint();
    if (this.state.panelOpen() && this.isDesktop()) {
      this.anchorToBell();
    }
  }

  public onItemSelect(id: string): void {
    this.state.markRead(id);
  }

  /**
   * Ação contextual (DEV-895, CA 01 e CA 02): marca como lida E abre o destino. Só fecha o painel
   * quando houve navegação — sem destino, fechar seria punir o clique com a perda do painel.
   *
   * A ORDEM importa e não é estilo: no filtro "Não lidas" o `markRead` remove o item da lista de
   * forma otimista e SÍNCRONA (`notifications-state.service.ts`, ramo `filter() === "unread"`), então
   * ler a lista depois dele devolveria `undefined` e o clique marcaria como lida sem navegar —
   * justamente no filtro em que a pessoa mais clica. O destino sai de `data`, que só existe no DTO:
   * `LktNotificationItem` carrega apenas o vocabulário visual.
   *
   * O `closePanel()` é explícito e não redundante com o fechamento por `NavigationEnd` do
   * construtor: navegar para a URL em que a pessoa já está não emite `NavigationEnd`, e o salto
   * entre apps troca o documento inteiro (nesse caso fechar é inócuo, não errado).
   */
  public onCtaSelect(id: string): void {
    const notification = this.state.items().find((item) => item.id === id);

    this.state.markRead(id);

    if (notification && this.navigation.open(notification)) {
      this.state.closePanel();
    }
  }

  /**
   * §9 da spec: o foco entra no painel ao abrir, e volta ao sino quando o fechamento vem do
   * `Escape`. No clique fora, NÃO: devolver o foco ali roubaria o foco do elemento clicado.
   */
  public onCloseRequest(reason: LktNotificationCloseReason): void {
    const bell = this.resolveAnchor();
    this.state.closePanel();
    if (reason === "escape") {
      bell?.focus();
    }
  }

  public onPreferencesOpen(): void {
    // Guardar ANTES de trocar a vista: depois da troca o corpo já é outro.
    this.cursor.listScrollTop = this.panelComponent()?.scrollTop() ?? 0;
    this.state.showPreferences();
  }

  public onFilterChange(next: LktNotificationFilter): void {
    this.state.setFilter(next);
  }

  /** Botão "tentar novamente" do estado de erro (achado 2, Important). */
  public onRetryLoad(): void {
    this.state.loadFirstPage();
  }

  /** Idem, na vista de preferências. */
  public onRetryPreferences(): void {
    this.state.retryPreferences();
  }

  /**
   * A inversão de polaridade do caminho de ESCRITA, e o único lugar em que ela acontece: a tela
   * fala de ligado, o contrato fala de desligado. A contraparte da leitura está em
   * `build-notification-preference-row.util.ts`.
   */
  public onPreferenceToggle(change: {
    readonly toggleKey: string;
    readonly channel: LktNotificationPreferenceChannel;
    readonly enabled: boolean;
  }): void {
    this.state.setPreference(change.toggleKey, change.channel, !change.enabled);
  }

  public itemDataCy(id: string): string {
    // O id é ObjectId hex de 24 chars: seguro em data-cy sem slugify.
    return `notification-item-${id}`;
  }

  /**
   * Deep link `?notificacoes=preferencias` (DEV-877): os dois links do rodapé do e-mail de
   * movimentação de equipe levam à home do app com esta query, e é ela que abre o painel já na
   * vista de preferências. Uma abertura por parâmetro: as duas portas de leitura
   * (`afterNextRender` e `NavigationEnd`) leem sempre o snapshot corrente, e quem chega depois da
   * limpeza não encontra mais nada — ver a trava `consumingDeepLink` em `cursor` para a janela em
   * que isso não bastaria.
   *
   * O parâmetro é CONSUMIDO: sai da URL numa navegação `replaceUrl` (sem entrada no histórico,
   * único precedente do repo — `clearQueryFlag` em `content-detail`), senão um F5 depois de fechar
   * o painel reabriria a vista que a pessoa acabou de fechar. `commands` vazio preserva os
   * segmentos correntes: quem chega pelo e-mail cai na home, mas o link pode um dia apontar para
   * outra tela e esta limpeza não deve mexer na rota.
   *
   * Abrir só DEPOIS que a limpeza terminou não é estilo: a navegação da limpeza emite o seu próprio
   * `NavigationEnd`, e o handler acima fecharia o painel aberto antes dela. A promessa do `navigate`
   * resolve depois do evento, então o `then` é o primeiro ponto seguro.
   */
  private consumeDeepLink(): void {
    if (this.cursor.consumingDeepLink) {
      return;
    }

    const requested = this.route.snapshot.queryParamMap.get(NOTIFICATIONS_QUERY_PARAM);
    if (requested !== NOTIFICATIONS_QUERY_PREFERENCES) {
      return;
    }

    this.cursor.consumingDeepLink = true;

    void this.router
      .navigate([], {
        queryParams: { [NOTIFICATIONS_QUERY_PARAM]: null },
        queryParamsHandling: "merge",
        replaceUrl: true
      })
      // A trava se solta no desfecho, seja ele qual for: presa, um deep link legítimo mais tarde na
      // mesma sessão (link interno carregando a query) seria engolido em silêncio.
      .then(
        () => {
          this.cursor.consumingDeepLink = false;
          this.state.openPreferences();
        },
        () => {
          this.cursor.consumingDeepLink = false;
        }
      );
  }

  /**
   * O sino a que o painel se ancora. O clique no sino já informa o elemento (`state.anchor()`); o
   * deep link não passa por clique nenhum, então cai na busca pelo sino VISÍVEL — medir um elemento
   * `display: none` devolve retângulo zerado, e é o CSS que decide qual das duas superfícies existe
   * em cada largura de tela.
   */
  private resolveAnchor(): HTMLElement | null {
    const clicked = this.state.anchor();
    if (clicked) {
      return clicked;
    }

    for (const selector of BELL_SELECTORS) {
      const bell = globalThis.document.querySelector<HTMLElement>(selector);
      if (bell && bell.offsetParent !== null) {
        return bell;
      }
    }

    return null;
  }

  /**
   * Minor 3 (fix round final): redimensionar de mobile para desktop com o painel aberto deixava-o
   * sem posição. `anchorToBell()` desiste em silêncio quando `bell.offsetParent === null` — exatamente
   * o estado do sino da bottom-nav acima de 767px —, e `.notifications-panel--floating` não declara
   * `top`/`left`. Fecha o painel quando o breakpoint de fato CRUZA (não a cada resize), em vez de
   * deixá-lo pintado no canto superior esquerdo até ser fechado manualmente.
   */
  private syncBreakpoint(): void {
    const wasMobile = this.isMobile();
    const isMobileNow =
      typeof globalThis.matchMedia === "undefined"
        ? false
        : globalThis.matchMedia(MOBILE_QUERY).matches;
    this.isMobile.set(isMobileNow);

    if (wasMobile !== isMobileNow && this.state.panelOpen()) {
      this.state.closePanel();
    }
  }

  /**
   * Encosta o painel no rail e centra verticalmente no sino, com clamp nos limites do rail.
   *
   * A ordem das escritas importa: o teto de altura vai primeiro, e só então lemos `offsetHeight`.
   * Essa leitura força o reflow, então a altura medida já é a que o teto permite.
   */
  private anchorToBell(): void {
    const bell = this.resolveAnchor();
    const panel = this.panelRef()?.nativeElement;
    if (!bell || !panel) {
      return;
    }
    // Quem ancora é o sino VISÍVEL: medir um elemento display:none devolve retângulo zerado.
    if (bell.offsetParent === null) {
      return;
    }
    const rail = bell.closest(RAIL_SELECTOR);
    if (!rail) {
      return;
    }

    const railRect = rail.getBoundingClientRect();
    const bellRect = bell.getBoundingClientRect();

    // Fonte única do tuck: o `padding-left` computado do painel (SCSS, --nt-tuck), não um literal
    // aqui.
    const tuck =
      Number.parseFloat(globalThis.getComputedStyle(panel).paddingLeft) || TUCK_FALLBACK_PX;

    // O teto é a altura do RAIL, sem cap contra a altura da janela — de propósito, e sem divergir do
    // protótipo: `js/notificacoes.js`, `ancorar()`, ramo do rail, faz exatamente `el.style.maxHeight
    // = Math.round(rr.height) + "px"`, sem `Math.min` contra `window.innerHeight` em lugar nenhum. O
    // `max-height: calc(100vh - 120px)` do SCSS (notification-panel.component.scss) só governa o
    // modo "flutuante solto" do protótipo (o painel medindo o SINO, não o rail) — modo que este app
    // não implementa; `anchorToBell()` só tem o caminho do rail (desktop) e o `nz-drawer` (mobile).
    // Medido em Chrome headless contra o `index.html` real do protótipo (20/08/2026, DEV-872): com
    // lista longa, o resultado é ~16px de folga em cima e embaixo, idêntico ao nosso — não os ~60px
    // que um cap de janela daria. Se o produto quiser mais respiro que isso, é uma decisão de UX
    // nova (divergir do protótipo), não uma correção de desvio — por isso não apliquei o
    // `Math.min(railRect.height, globalThis.innerHeight - 120)` cogitado a princípio.
    panel.style.maxHeight = `${Math.round(railRect.height)}px`;
    panel.style.left = `${Math.round(railRect.right - tuck)}px`;

    const panelHeight = panel.offsetHeight || 0;
    const centered = Math.round(bellRect.top + bellRect.height / 2 - panelHeight / 2);
    const clamped = Math.max(
      Math.round(railRect.top),
      Math.min(centered, Math.round(railRect.bottom - panelHeight))
    );
    panel.style.top = `${clamped}px`;
  }
}
